import React, { useEffect, useMemo, useRef, useState } from 'react';

export const FilterSelect = ({
    value,
    options,
    onChange,
    placeholder = 'Seleccionar...',
    searchable = false,
    searchPlaceholder = 'Buscar...',
    disabled = false,
    menuPlacement = 'auto',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [resolvedPlacement, setResolvedPlacement] = useState('bottom');
    const containerRef = useRef(null);
    const menuRef = useRef(null);

    const getInitials = (text = '') => {
        const safeText = String(text || '').trim();
        if (!safeText) {
            return '•';
        }

        const words = safeText.split(/\s+/).filter(Boolean);
        if (words.length === 1) {
            return words[0].slice(0, 2).toUpperCase();
        }

        return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
    };

    const selectedOption = useMemo(() => {
        return options.find((option) => option.value === value) || null;
    }, [options, value]);

    const filteredOptions = useMemo(() => {
        const normalizedQuery = String(query || '').trim().toLowerCase();
        if (!normalizedQuery) {
            return options;
        }

        return options.filter((option) => {
            const label = String(option?.label || '').toLowerCase();
            const meta = String(option?.meta || '').toLowerCase();
            return label.includes(normalizedQuery) || meta.includes(normalizedQuery);
        });
    }, [options, query]);

    const groupedOptions = useMemo(() => {
        const hasGroups = filteredOptions.some((option) => Boolean(option?.group));
        if (!hasGroups) {
            return [];
        }

        const groupsMap = new Map();
        filteredOptions.forEach((option) => {
            const groupKey = String(option?.group || 'Otros');
            if (!groupsMap.has(groupKey)) {
                groupsMap.set(groupKey, []);
            }

            groupsMap.get(groupKey).push(option);
        });

        return Array.from(groupsMap.entries()).map(([group, rows]) => ({
            group,
            rows,
        }));
    }, [filteredOptions]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const resolvePlacement = () => {
            if (menuPlacement === 'top' || menuPlacement === 'bottom') {
                setResolvedPlacement(menuPlacement);
                return;
            }

            const container = containerRef.current;
            if (!container) {
                setResolvedPlacement('bottom');
                return;
            }

            const rect = container.getBoundingClientRect();
            const menuHeight = menuRef.current?.offsetHeight || 260;
            const viewportPadding = 10;
            const availableBelow = window.innerHeight - rect.bottom - viewportPadding;
            const availableAbove = rect.top - viewportPadding;

            if (availableBelow < menuHeight && availableAbove > availableBelow) {
                setResolvedPlacement('top');
                return;
            }

            setResolvedPlacement('bottom');
        };

        const rafId = window.requestAnimationFrame(resolvePlacement);
        resolvePlacement();
        window.addEventListener('resize', resolvePlacement);
        window.addEventListener('scroll', resolvePlacement, true);

        return () => {
            window.cancelAnimationFrame(rafId);
            window.removeEventListener('resize', resolvePlacement);
            window.removeEventListener('scroll', resolvePlacement, true);
        };
    }, [isOpen, menuPlacement]);

    const handleToggle = () => {
        if (disabled) {
            return;
        }

        setIsOpen((prev) => !prev);
        if (isOpen) {
            setQuery('');
        }
    };

    const handleSelect = (nextValue) => {
        onChange(nextValue);
        setIsOpen(false);
        setQuery('');
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
            setQuery('');
        }

        if (disabled) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen((prev) => !prev);
        }
    };

    const renderOptionContent = (option, isSelectedValue = false) => {
        const hasMedia = Boolean(option?.avatar || option?.icon);
        const hasMeta = Boolean(option?.meta);

        if (!hasMedia && !hasMeta) {
            return option?.label;
        }

        return (
            <span className="filter-select__option-content">
                {option?.avatar ? (
                    <img
                        src={option.avatar}
                        alt={option?.label || 'Usuario'}
                        className="filter-select__avatar"
                        onError={(event) => {
                            event.currentTarget.style.display = 'none';
                            const fallback = event.currentTarget.nextElementSibling;
                            if (fallback) {
                                fallback.style.display = 'inline-flex';
                            }
                        }}
                    />
                ) : null}

                <span
                    className="filter-select__avatar-fallback"
                    style={{ display: option?.avatar ? 'none' : 'inline-flex' }}
                >
                    {option?.icon ? <i className={option.icon} aria-hidden="true" /> : getInitials(option?.label)}
                </span>

                <span className="filter-select__text-group">
                    <span className={`filter-select__option-label ${isSelectedValue ? 'is-selected' : ''}`}>{option?.label}</span>
                    {option?.meta ? <span className="filter-select__option-meta">{option.meta}</span> : null}
                </span>
            </span>
        );
    };

    return (
        <div className={`filter-select ${isOpen ? 'is-open' : ''} w-100`} ref={containerRef}>
            <button
                type="button"
                className={`filter-select__trigger ${isOpen ? 'is-open' : ''}`}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                disabled={disabled}
            >
                <span className="filter-select__value">
                    {selectedOption ? renderOptionContent(selectedOption, true) : placeholder}
                </span>
                <i className={`feather-chevron-down filter-select__icon ${isOpen ? 'is-open' : ''}`}></i>
            </button>

            {isOpen && (
                <div
                    ref={menuRef}
                    className={`filter-select__menu ${resolvedPlacement === 'top' ? 'is-top' : 'is-bottom'}`}
                    role="listbox"
                >
                    {searchable && (
                        <div className="filter-select__search-wrap">
                            <input
                                type="search"
                                className="filter-select__search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value || '')}
                                placeholder={searchPlaceholder}
                                autoFocus
                            />
                        </div>
                    )}

                    {groupedOptions.length > 0 ? groupedOptions.map(({ group, rows }) => (
                        <div key={group} className="filter-select__group">
                            <div className="filter-select__group-label">{group}</div>
                            {rows.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`filter-select__option ${option.value === value ? 'is-selected' : ''}`}
                                    onClick={() => handleSelect(option.value)}
                                >
                                    {renderOptionContent(option, option.value === value)}
                                </button>
                            ))}
                        </div>
                    )) : filteredOptions.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={`filter-select__option ${option.value === value ? 'is-selected' : ''}`}
                            onClick={() => handleSelect(option.value)}
                        >
                            {renderOptionContent(option, option.value === value)}
                        </button>
                    ))}

                    {filteredOptions.length === 0 && (
                        <div className="filter-select__empty">Sin resultados</div>
                    )}
                </div>
            )}
        </div>
    );
};
